import type { OrganizationType } from '@kanbu/schema';
import {
  createOrganizationSchema,
  updateOrganizationSchema,
} from '@kanbu/schema/contracts';
import {
  Role,
  Currency,
  Locale,
  SubscriptionStatus,
} from '@kanbu/schema/enums';
import { formatUtils } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { Button, cn } from '@utima/ui';
import { Pencil, Users, File } from 'lucide-react';
import { memo } from 'react';

import { Card } from '@/components/card/Card';
import { DisplayValue } from '@/components/displayValue/DisplayValue';
import { MetaFields } from '@/components/fields/MetaFields';
import { EntityCRUDForm } from '@/components/form/EntityCRUDForm';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { Protected } from '@/components/protected/Protected';
import { useUser } from '@/hooks';

import { OrganizationBillingInfo } from './OrganizationBillingInfo';
import { TierConfigFields } from './TierConfigFields';
import { WorkingHours } from './workingHours/WorkingHours';
import { useOrganizationMutation } from '../hooks/useOrganizationMutation';

export type OrganizationFormProps = {
  data?: OrganizationType;
  organizationId?: string;
};

export const OrganizationForm = memo(function OrganizationForm({
  data,
}: OrganizationFormProps) {
  const { t } = useLingui();
  const orgCurrency = data?.currency ?? Currency.USD;
  const { user, currentOrganizationId } = useUser();
  const {
    create: { mutateAsync: createOrg },
    remove: { mutateAsync: removeOrg },
    update: { mutateAsync: updateOrg },
  } = useOrganizationMutation();

  const { schema: orgSettingsSchema } = useFieldsSchema(
    [
      {
        name: 'margin',
        label: t`Margin`,
        type: 'number',
        uiProps: {
          step: '0.01',
          min: '1',
        },
        defaultValue: 5,
        tooltip: t`Multiplier applied to base usage costs (e.g., 1.5 = 50% markup). Minimum 1.`,
        required: true,
      },
      {
        name: 'currency',
        label: t`Billing Currency`,
        type: 'select',
        defaultValue: data?.currency ?? Currency.CZK,
        options: {
          [Currency.USD]: 'USD ($)',
          [Currency.CZK]: 'CZK (Kč)',
        },
        required: true,
        tooltip: t`Select the primary currency for billing and balance for this organization.`,
      },
    ],
    [t, data?.currency],
  );

  const { schema: orgLocaleSchema } = useFieldsSchema(
    [
      {
        name: 'locale',
        label: t`Communication Language`,
        type: 'select',
        defaultValue: data?.locale ?? Locale.Cs,
        options: {
          [Locale.Cs]: 'Čeština',
          [Locale.En]: 'English',
        },
        required: true,
        tooltip: t`Language used for invoice emails, payment reminders, and the Hosted Invoice Page sent by Stripe.`,
      },
    ],
    [t, data?.locale],
  );

  if (!currentOrganizationId) {
    throw new Error('No current organization id');
  }

  const current = data?.currentMonthlyUsage ?? 0;
  const limit = data?.monthlyUsageLimit ?? 0;
  const activeSubscription = data?.subscriptions.find(
    sub => sub.status === SubscriptionStatus.Active,
  );

  return (
    <EntityCRUDForm
      initialData={data}
      createSchema={createOrganizationSchema}
      updateSchema={updateOrganizationSchema}
      onCreate={async values => {
        const result = await createOrg(values);

        return result.organization as unknown as OrganizationType;
      }}
      onUpdate={async values => {
        const marginUpdate =
          user?.role === Role.SuperAdmin && values.margin !== undefined
            ? { margin: values.margin }
            : {};
        const updateValues = { ...values, ...marginUpdate };

        return updateOrg(updateValues);
      }}
      onRemove={async ({ id }) => {
        await removeOrg({ id });
      }}
      basePath='/organizations'
      idParamName='organizationId'
      deleteDescription={t`Are you sure you want to delete this organization? Any remaining credits will be permanently lost.`}
      customRedirect='/inbox'
      confirmationText={data?.name}
      confirmationLabel={t`Type the organization name to confirm:`}
    >
      <EntityCRUDForm.Col>
        <TierConfigFields />
        <Card title={t`Communication`} className='mt-4 space-y-1'>
          <SchemaFields schema={orgLocaleSchema} />
        </Card>
        <Protected roles={[Role.SuperAdmin]}>
          <Card title={t`Billing Settings`} className='mt-4 space-y-1'>
            <SchemaFields schema={orgSettingsSchema} />
          </Card>
        </Protected>
      </EntityCRUDForm.Col>
      <EntityCRUDForm.Col>
        <WorkingHours name='workingHours' />
      </EntityCRUDForm.Col>
      <EntityCRUDForm.Col>
        <MetaFields data={data}>
          <DisplayValue label={t`Monthly usage limit`}>
            <span
              className={cn(
                'inline-flex gap-1 items-baseline',
                current > limit ? 'text-red-600' : 'text-green-600',
              )}
            >
              <span className='font-semibold'>
                {formatUtils.credits(current, orgCurrency)}
              </span>
              /
              <span className='text-xs font-semibold'>
                {formatUtils.credits(limit, orgCurrency)}
              </span>
            </span>
          </DisplayValue>
          <DisplayValue label={t`Prepaid balance`}>
            <span
              className={cn(
                'font-semibold text-green-600',
                (data?.balance ?? 0) > 0 ? 'text-green-600' : '',
              )}
            >
              <span className='font-semibold'>
                {formatUtils.credits(data?.balance ?? 0, orgCurrency)}
              </span>
            </span>
          </DisplayValue>
          <DisplayValue label={t`Current users limit`}>
            <div className='flex flex-row gap-2'>
              <Users />
              <span>{activeSubscription?.maxUsers}</span>
            </div>
          </DisplayValue>
          <DisplayValue label={t`Current documents limit`}>
            <div className='flex flex-row gap-2'>
              <File />
              <span>{activeSubscription?.maxDocuments}</span>
            </div>
          </DisplayValue>

          <DisplayValue label={t`Billing Information`}>
            <div className='flex flex-col items-start gap-2'>
              {data?.billingInfo && (
                <OrganizationBillingInfo billingInfo={data.billingInfo} />
              )}
              <Button
                data-cta='admin-organization-form-billing-info-button'
                variant='secondary'
                size='sm'
                asChild
                icon={<Pencil size={16} />}
              >
                <Link
                  to='/organizations/$organizationId/billing'
                  params={{
                    organizationId: currentOrganizationId,
                  }}
                >
                  {data?.billingInfo
                    ? t`Edit Billing Information`
                    : t`Set Billing Information`}
                </Link>
              </Button>
            </div>
          </DisplayValue>
        </MetaFields>
      </EntityCRUDForm.Col>
    </EntityCRUDForm>
  );
});
